import { createHash, randomBytes, timingSafeEqual } from "node:crypto";
import { createServer, type IncomingMessage, type ServerResponse } from "node:http";
import { AddressInfo } from "node:net";
import { MCP_HOME } from "./urls.js";
import {
  clearCredentials,
  isExpired,
  readCredentials,
  writeCredentials,
  type StoredCredentials,
} from "./credentials.js";

/**
 * Interactive OAuth 2.1 login against the Carrier MCP authorization server.
 *
 * The server already speaks the full MCP OAuth profile — Dynamic Client
 * Registration, PKCE S256, authorization_code + refresh_token, and public
 * clients (`token_endpoint_auth_methods_supported` includes "none"). That is
 * exactly what an installed CLI needs, so this registers itself at runtime
 * rather than shipping a baked-in client_id.
 *
 * Access tokens live 15 minutes, so silent refresh is not a nicety: without it
 * the CLI would demand a browser every quarter of an hour.
 */

const DISCOVERY_PATH = "/.well-known/oauth-authorization-server";
const CLIENT_NAME = "Carrier CLI";
const DEFAULT_SCOPE = "read write";

/** Ports tried in order for the loopback redirect. */
const CALLBACK_PORTS = [3118, 3119, 8976, 8977, 0];

export interface AuthServerMetadata {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  registration_endpoint?: string;
  scopes_supported?: string[];
  code_challenge_methods_supported?: string[];
}

export interface LoginResult {
  ok: boolean;
  /** Safe to print — never contains a token. */
  message: string;
  scope?: string;
}

export async function discover(base = MCP_HOME): Promise<AuthServerMetadata> {
  const res = await fetch(`${base}${DISCOVERY_PATH}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`OAuth discovery failed (${res.status}) at ${base}${DISCOVERY_PATH}`);
  const meta = (await res.json()) as AuthServerMetadata;
  if (!meta.authorization_endpoint || !meta.token_endpoint) {
    throw new Error("OAuth discovery returned no authorization or token endpoint");
  }
  return meta;
}

function base64url(buf: Buffer): string {
  return buf.toString("base64").replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function pkcePair(): { verifier: string; challenge: string } {
  const verifier = base64url(randomBytes(32));
  const challenge = base64url(createHash("sha256").update(verifier).digest());
  return { verifier, challenge };
}

/** Constant-time compare so a returned `state` cannot be probed byte by byte. */
function sameState(a: string, b: string): boolean {
  const left = Buffer.from(a);
  const right = Buffer.from(b);
  if (left.length !== right.length) return false;
  return timingSafeEqual(left, right);
}

async function registerClient(
  meta: AuthServerMetadata,
  redirectUri: string,
): Promise<string> {
  if (!meta.registration_endpoint) {
    throw new Error("This server does not support dynamic client registration");
  }
  const res = await fetch(meta.registration_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      client_name: CLIENT_NAME,
      redirect_uris: [redirectUri],
      grant_types: ["authorization_code", "refresh_token"],
      response_types: ["code"],
      // Installed apps cannot keep a secret; register as a public client.
      token_endpoint_auth_method: "none",
      application_type: "native",
    }),
  });
  if (!res.ok) {
    const body = (await res.text().catch(() => "")).slice(0, 200);
    throw new Error(`Client registration failed (${res.status}). ${body}`);
  }
  const json = (await res.json()) as { client_id?: string };
  if (!json.client_id) throw new Error("Client registration returned no client_id");
  return json.client_id;
}

interface CallbackServer {
  redirectUri: string;
  /** Resolves with the authorization code once the browser hits /callback. */
  waitForCode: (expectedState: string, timeoutMs: number) => Promise<string>;
  close: () => void;
}

/** Escapes text dropped into the loopback callback page — some of it (the
 * OAuth `error` param) comes straight off the request URL. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function page(title: string, detail: string): string {
  const safeTitle = escapeHtml(title);
  const safeDetail = escapeHtml(detail);
  return `<!doctype html><meta charset="utf-8"><title>${safeTitle}</title>
<body style="font-family:system-ui,sans-serif;max-width:32rem;margin:20vh auto;text-align:center">
<h1 style="font-size:1.25rem">${safeTitle}</h1><p style="color:#555">${safeDetail}</p></body>`;
}

async function startCallbackServer(): Promise<CallbackServer> {
  let resolveCode: ((code: string) => void) | undefined;
  let rejectCode: ((err: Error) => void) | undefined;
  let expected = "";

  const server = createServer((req: IncomingMessage, res: ServerResponse) => {
    const url = new URL(req.url ?? "/", "http://127.0.0.1");
    if (url.pathname !== "/callback") {
      res.writeHead(404).end();
      return;
    }
    const err = url.searchParams.get("error");
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state") ?? "";

    if (err) {
      res.writeHead(400, { "Content-Type": "text/html" });
      res.end(page("Sign-in failed", err));
      rejectCode?.(new Error(`Authorization server returned "${err}"`));
      return;
    }
    if (!code || !expected || !sameState(state, expected)) {
      res.writeHead(400, { "Content-Type": "text/html" });
      res.end(page("Sign-in failed", "The redirect did not match this login attempt."));
      rejectCode?.(new Error("State mismatch — ignoring the redirect"));
      return;
    }
    res.writeHead(200, { "Content-Type": "text/html" });
    res.end(page("You're signed in", "Close this tab and return to your terminal."));
    resolveCode?.(code);
  });

  let bound = false;
  let lastErr: unknown;
  for (const port of CALLBACK_PORTS) {
    try {
      await new Promise<void>((resolve, reject) => {
        server.once("error", reject);
        server.listen(port, "127.0.0.1", () => {
          server.removeListener("error", reject);
          resolve();
        });
      });
      bound = true;
      break;
    } catch (e) {
      lastErr = e;
    }
  }
  if (!bound) {
    throw new Error(
      `Could not bind a local callback port (tried ${CALLBACK_PORTS.filter(Boolean).join(", ")}): ${
        lastErr instanceof Error ? lastErr.message : String(lastErr)
      }`,
    );
  }

  const { port } = server.address() as AddressInfo;
  return {
    redirectUri: `http://localhost:${port}/callback`,
    // `close()` alone only stops new connections; a keep-alive socket from the
    // browser would hold the event loop open and the CLI would never exit.
    waitForCode: (expectedState, timeoutMs) => {
      expected = expectedState;
      return new Promise<string>((resolve, reject) => {
        // Left running, this timer keeps the process alive for the full
        // timeout after a successful login — the CLI would appear to hang.
        const timer = setTimeout(
          () => reject(new Error(`Timed out after ${Math.round(timeoutMs / 1000)}s waiting for the browser`)),
          timeoutMs,
        );
        resolveCode = (code) => {
          clearTimeout(timer);
          resolve(code);
        };
        rejectCode = (err) => {
          clearTimeout(timer);
          reject(err);
        };
      });
    },
    close: () => {
      server.closeAllConnections();
      server.close();
    },
  };
}

interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  scope?: string;
  error?: string;
  error_description?: string;
}

async function postToken(
  meta: AuthServerMetadata,
  body: Record<string, string>,
): Promise<TokenResponse> {
  const res = await fetch(meta.token_endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body: new URLSearchParams(body).toString(),
  });
  const json = (await res.json().catch(() => ({}))) as TokenResponse;
  if (!res.ok || json.error) {
    throw new Error(json.error_description || json.error || `Token endpoint returned ${res.status}`);
  }
  if (!json.access_token) throw new Error("Token endpoint returned no access_token");
  return json;
}

function store(meta: AuthServerMetadata, token: TokenResponse, clientId: string): StoredCredentials {
  return {
    issuer: meta.issuer,
    accessToken: token.access_token!,
    refreshToken: token.refresh_token,
    expiresAt: Date.now() + (token.expires_in ?? 900) * 1000,
    scope: token.scope,
    clientId,
  };
}

/**
 * Full interactive login. `openBrowser` is injected so the caller owns the UX
 * (and tests can drive it without a browser).
 */
export async function login(opts: {
  openBrowser: (url: string) => Promise<unknown>;
  onUrl?: (url: string) => void;
  timeoutMs?: number;
}): Promise<LoginResult> {
  const meta = await discover();
  const server = await startCallbackServer();
  try {
    const existing = await readCredentials();
    const clientId =
      existing?.clientId && existing.issuer === meta.issuer
        ? existing.clientId
        : await registerClient(meta, server.redirectUri);

    const { verifier, challenge } = pkcePair();
    const state = base64url(randomBytes(16));

    const authUrl = new URL(meta.authorization_endpoint);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("client_id", clientId);
    authUrl.searchParams.set("redirect_uri", server.redirectUri);
    authUrl.searchParams.set("scope", pickScope(meta));
    authUrl.searchParams.set("state", state);
    authUrl.searchParams.set("code_challenge", challenge);
    authUrl.searchParams.set("code_challenge_method", "S256");

    // Arm the callback listener BEFORE the browser is opened. Doing it after
    // is a race: a fast redirect arrives while the handler still has no
    // expected state, gets rejected as a mismatch, and the CLI then waits for
    // a code that already came and went.
    const pendingCode = server.waitForCode(state, opts.timeoutMs ?? 300_000);
    // The redirect can fail before anything awaits `pendingCode` (a rejected
    // state arrives while the browser is still being opened). Mark it handled
    // so that surfaces as a normal rejection below, not an unhandled one.
    pendingCode.catch(() => undefined);

    opts.onUrl?.(authUrl.toString());
    await opts.openBrowser(authUrl.toString());

    const code = await pendingCode;
    const token = await postToken(meta, {
      grant_type: "authorization_code",
      code,
      redirect_uri: server.redirectUri,
      client_id: clientId,
      code_verifier: verifier,
    });

    await writeCredentials(store(meta, token, clientId));
    return { ok: true, message: "Signed in.", scope: token.scope };
  } finally {
    server.close();
  }
}

function pickScope(meta: AuthServerMetadata): string {
  const supported = meta.scopes_supported ?? [];
  if (!supported.length) return DEFAULT_SCOPE;
  // Ask only for what we need; `admin` requires MFA step-up server-side.
  return DEFAULT_SCOPE.split(" ")
    .filter((s) => supported.includes(s))
    .join(" ") || supported[0]!;
}

/**
 * Return a usable access token from the credential store, refreshing first when
 * it is at or near expiry. Returns null when the user has never logged in.
 */
export async function getAccessToken(): Promise<string | null> {
  const creds = await readCredentials();
  if (!creds) return null;
  if (!isExpired(creds)) return creds.accessToken;
  if (!creds.refreshToken || !creds.clientId) return null;

  try {
    const meta = await discover();
    const token = await postToken(meta, {
      grant_type: "refresh_token",
      refresh_token: creds.refreshToken,
      client_id: creds.clientId,
    });
    const next = store(meta, token, creds.clientId);
    // Servers may omit a rotated refresh token; keep the old one if so.
    if (!next.refreshToken) next.refreshToken = creds.refreshToken;
    await writeCredentials(next);
    return next.accessToken;
  } catch {
    // A dead refresh token should read as "logged out", not as a hard failure.
    return null;
  }
}

export async function logout(): Promise<void> {
  await clearCredentials();
}
